import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RatingReadout from '../../components/common/RatingReadout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ADD_FITNESS_WORKOUT,
  DELETE_FITNESS_WORKOUT,
  GET_FITNESS_WORKOUT,
  GET_FITNESS_WORKOUTS,
  GET_FITNESS_PLANS,
  GET_FITNESS_ROUTINE_HISTORY,
  GET_FITNESS_EXERCISE_CATALOG,
  ADD_FITNESS_PLAN,
  UPDATE_FITNESS_PLAN,
  DELETE_FITNESS_PLAN,
  START_FITNESS_WORKOUT,
  LOG_FITNESS_ROUTINE_WORKOUT,
  UPDATE_FITNESS_WORKOUT_SESSION,
  FINISH_FITNESS_WORKOUT,
  DISCARD_FITNESS_WORKOUT,
  UPDATE_FITNESS_WORKOUT,
} from './operations';
import FitnessForm from './FitnessForm';
import FitnessRoutineForm from './FitnessRoutineForm';
import FitnessExerciseEditor, { normalizeExercise } from './FitnessExerciseEditor';
import FitnessRoutineDetail from './FitnessRoutineDetail';
import { fitnessStrings, moduleCommonStrings } from '../../content/hobbyModules';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import EntryImage from '../../components/common/EntryImage';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries } from '../../utils/subscriptionLimits';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';

const FITNESS_PAGE_SIZE = 20;

/**
 * Performs the local date key operation.
 *
 * @param {*} date - date value.
 * @returns {*} The derived operation result.
 */
function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Performs the elapsed session time operation.
 *
 * @param {*} startedAt - started at value.
 * @param {*} now - now value.
 * @returns {*} The derived operation result.
 */
function elapsedSessionTime(startedAt, now) {
  const started = Date.parse(startedAt || '');
  if (!Number.isFinite(started)) return 'In progress';
  const minutes = Math.max(Math.floor((now - started) / 60000), 0);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

const defaultWorkout = {
  title: '',
  mode: 'structured',
  workoutType: 'Strength',
  workoutDate: '',
  scheduledAt: '',
  completedAt: '',
  activityType: 'Strength',
  durationMinutes: 0,
  intensity: 'Moderate',
  caloriesBurned: 0,
  distance: '',
  distanceUnit: 'mi',
  pace: '',
  averageHeartRate: 0,
  maxHeartRate: 0,
  elevationGain: 0,
  equipment: [],
  muscleGroups: [],
  strengthExercises: [],
  focusAreas: [],
  className: '',
  location: '',
  coach: '',
  status: 'Completed',
  plannedDate: '',
  completedDate: '',
  planName: '',
  templateName: '',
  energyLevel: '',
  sorenessLevel: '',
  recoveryNotes: '',
  rating: '',
  review: '',
  notes: '',
  tags: [],
  favorite: false,
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

/**
 * Normalizes workout.
 *
 * @param {*} workout - workout value.
 * @returns {*} The derived operation result.
 */
function parseWorkout(workout) {
  const safeWorkout = workout || {};
  const scheduledAt = safeWorkout.scheduledAt || safeWorkout.plannedDate || '';
  const completedAt = safeWorkout.completedAt || safeWorkout.completedDate || '';
  return {
    ...defaultWorkout,
    ...safeWorkout,
    title: safeWorkout.title || fitnessStrings.module.untitled,
    mode: safeWorkout.mode || (['Walk', 'Run', 'Cycling', 'Swim', 'Sport', 'Cardio'].includes(safeWorkout.workoutType) ? 'session' : 'structured'),
    scheduledAt,
    completedAt,
    workoutDate: safeWorkout.workoutDate || completedAt || scheduledAt,
    durationMinutes: Number(safeWorkout.durationMinutes || 0),
    caloriesBurned: Number(safeWorkout.caloriesBurned || 0),
    averageHeartRate: Number(safeWorkout.averageHeartRate || 0),
    maxHeartRate: Number(safeWorkout.maxHeartRate || 0),
    elevationGain: Number(safeWorkout.elevationGain || 0),
    equipment: Array.isArray(safeWorkout.equipment) ? safeWorkout.equipment.filter(Boolean) : splitCsv(safeWorkout.equipment),
    muscleGroups: Array.isArray(safeWorkout.muscleGroups) ? safeWorkout.muscleGroups.filter(Boolean) : splitCsv(safeWorkout.muscleGroups),
    strengthExercises: Array.isArray(safeWorkout.strengthExercises) ? safeWorkout.strengthExercises : [],
    focusAreas: Array.isArray(safeWorkout.focusAreas) ? safeWorkout.focusAreas.filter(Boolean) : splitCsv(safeWorkout.focusAreas),
    tags: Array.isArray(safeWorkout.tags) ? safeWorkout.tags.filter(Boolean) : splitCsv(safeWorkout.tags),
    rating: safeWorkout.rating || '',
    review: safeWorkout.review || '',
    favorite: Boolean(safeWorkout.favorite),
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Normalizes date.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatDate(value) {
  if (!value) return fitnessStrings.module.noDate;
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Performs the minutes label operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function minutesLabel(value) {
  const minutes = Number(value || 0);
  return minutes ? `${minutes} min` : fitnessStrings.module.noDuration;
}

/**
 * Performs the primary date operation.
 *
 * @param {*} workout - workout value.
 * @returns {*} The derived operation result.
 */
function primaryDate(workout) {
  return workout.status === 'Completed'
    ? workout.completedAt || workout.completedDate || workout.workoutDate
    : workout.scheduledAt || workout.plannedDate || workout.workoutDate;
}

/**
 * Performs the mode label operation.
 *
 * @param {*} mode - mode value.
 * @returns {*} The derived operation result.
 */
function modeLabel(mode) {
  return mode === 'session' ? 'Activity session' : 'Structured workout';
}

/**
 * Performs the workout metric operation.
 *
 * @param {*} workout - workout value.
 * @returns {*} The derived operation result.
 */
function workoutMetric(workout) {
  if (workout.mode === 'session') {
    return `${workout.distance || ''} ${workout.distanceUnit || ''}`.trim();
  }
  const exerciseCount = workout.strengthExercises.filter((exercise) => exercise.name).length;
  return exerciseCount > 0 ? `${exerciseCount} ${exerciseCount === 1 ? 'exercise' : 'exercises'}` : '';
}

/**
 * Performs the compact detail items operation.
 *
 * @param {Array<*>} items - items collection.
 * @returns {*} The derived operation result.
 */
function compactDetailItems(items) {
  return items.filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
}

/** Owns the combined activity/routine list, workout sessions, history, and fitness mutations. */
export default function FitnessModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetItemId = searchParams.get('itemId');
  const openedTargetRef = useRef(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [workoutPendingDelete, setWorkoutPendingDelete] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeMode, setActiveMode] = useState('');
  const [activeType, setActiveType] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planPendingDelete, setPlanPendingDelete] = useState(null);
  const [startingPlan, setStartingPlan] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionClock, setSessionClock] = useState(Date.now());
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const isRoutineRoute = routeParts[0] === 'routine';
  const isRoutineCreateRoute = isRoutineRoute && routeParts[1] === 'new';
  const routePlanId = isRoutineRoute && routeParts[1] !== 'new' ? routeParts[1] : '';
  const isRoutineEditRoute = Boolean(routePlanId && routeParts[2] === 'edit');
  const routeWorkoutId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const normalizedWorkoutId = isRoutineRoute ? '' : routeWorkoutId;
  const isEditRoute = Boolean(normalizedWorkoutId && routeParts[1] === 'edit');

  useEffect(() => {
    if (!activeSession) return undefined;
    setSessionClock(Date.now());
    const timer = window.setInterval(() => setSessionClock(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [activeSession?.id]);
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];

  const { loading, error, data, fetchMore } = useQuery(GET_FITNESS_WORKOUTS, {
    variables: { first: FITNESS_PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: plansData, loading: plansLoading, error: plansError } = useQuery(GET_FITNESS_PLANS, { errorPolicy: 'all' });
  const { data: routineHistoryData, loading: routineHistoryLoading, refetch: refetchRoutineHistory, fetchMore: fetchMoreRoutineHistory } = useQuery(GET_FITNESS_ROUTINE_HISTORY, {
    variables: { planId: selectedPlan?.id || '', first: 10, after: null },
    skip: !selectedPlan?.id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { data: catalogData } = useQuery(GET_FITNESS_EXERCISE_CATALOG, { errorPolicy: 'all' });
  const plans = useMemo(() => plansData?.getFitnessPlans || [], [plansData]);
  const exerciseCatalog = useMemo(() => catalogData?.getFitnessExerciseCatalog || [], [catalogData]);
  const [loadWorkoutDetail] = useLazyQuery(GET_FITNESS_WORKOUT, { fetchPolicy: 'network-only' });
  const workouts = useMemo(() => (data?.getFitnessWorkoutsListPage?.nodes || []).map(parseWorkout), [data]);
  const workoutsPageInfo = data?.getFitnessWorkoutsListPage?.pageInfo || {};
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const {
    loading: isLoadingSelectedWorkout,
    data: selectedWorkoutData,
  } = useQuery(GET_FITNESS_WORKOUT, {
    variables: { id: selectedWorkout?.id || '' },
    skip: !selectedWorkout?.id,
    errorPolicy: 'all',
  });
  const selectedWorkoutRecord = selectedWorkoutData && selectedWorkoutData.getFitnessWorkout;
  const selectedWorkoutDetail = selectedWorkoutRecord && selectedWorkoutRecord.id === selectedWorkout?.id
    ? parseWorkout(selectedWorkoutRecord)
    : selectedWorkout && parseWorkout(selectedWorkout);
  const activityTypes = useMemo(() => uniqueValues(workouts, (workout) => [workout.workoutType || workout.activityType]), [workouts]);
  const tags = useMemo(() => uniqueValues(workouts, (workout) => workout.tags), [workouts]);

  const sortedWorkouts = useMemo(() => (
    [...workouts].sort((a, b) => String(primaryDate(b) || '').localeCompare(String(primaryDate(a) || '')))
  ), [workouts]);

  const filteredWorkouts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortedWorkouts.filter((workout) => {
      const searchable = [
        workout.title,
        modeLabel(workout.mode),
        workout.workoutType,
        primaryDate(workout),
        workout.activityType,
        workout.intensity,
        workout.distance,
        workout.muscleGroups.join(' '),
        workout.status,
        workout.notes,
        workout.review,
        workout.tags.join(' '),
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeMode
          ? (!workout.planId || workout.status === 'In Progress')
          : workout.mode === activeMode) &&
        (!activeType || workout.workoutType === activeType || workout.activityType === activeType) &&
        (!activeTag || workout.tags.includes(activeTag)) &&
        (!favoritesOnly || workout.favorite)
      );
    });
  }, [activeMode, activeTag, activeType, favoritesOnly, search, sortedWorkouts]);

  const filteredPlans = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (activeMode && activeMode !== 'routine') return [];
    if (activeType || activeTag) return [];
    return plans.filter((plan) => {
      const searchable = [plan.title, plan.goal, plan.status, ...(plan.days || []).map((day) => `${day.title} ${day.weekday}`)].join(' ').toLowerCase();
      return (!query || searchable.includes(query)) && (!favoritesOnly || plan.favorite);
    });
  }, [activeMode, activeTag, activeType, favoritesOnly, plans, search]);

  const totalMinutes = workouts.reduce((sum, workout) => sum + Number(workout.durationMinutes || 0), 0);

  const [addWorkout, { loading: isSaving }] = useMutation(ADD_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setHasFormChanges(false);
      onClose();
    },
  });

  const [updateWorkout, { loading: isUpdating }] = useMutation(UPDATE_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      setEditingWorkout(null);
      setHasFormChanges(false);
      if (result?.updateFitnessWorkout) {
        setSelectedWorkout(parseWorkout(result.updateFitnessWorkout));
      }
    },
  });

  const [deleteWorkout, { loading: isDeleting }] = useMutation(DELETE_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setWorkoutPendingDelete(null);
      setSelectedWorkout(null);
      navigate('/hobby/fitness');
    },
  });

  const [addPlan, { loading: isAddingPlan }] = useMutation(ADD_FITNESS_PLAN, {
    refetchQueries: [{ query: GET_FITNESS_PLANS }, ...subscriptionRefetch],
  });
  const [updatePlan, { loading: isUpdatingPlan }] = useMutation(UPDATE_FITNESS_PLAN, {
    refetchQueries: [{ query: GET_FITNESS_PLANS }],
  });
  const [deletePlan, { loading: isDeletingPlan }] = useMutation(DELETE_FITNESS_PLAN, {
    refetchQueries: [{ query: GET_FITNESS_PLANS }, ...subscriptionRefetch],
  });
  const [startWorkout, { loading: isStartingWorkout }] = useMutation(START_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }],
  });
  const [logRoutineWorkout] = useMutation(LOG_FITNESS_ROUTINE_WORKOUT, {
    refetchQueries: [
      { query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } },
      { query: GET_FITNESS_PLANS },
    ],
  });
  const [finishWorkout, { loading: isFinishingWorkout }] = useMutation(FINISH_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }, { query: GET_FITNESS_PLANS }],
  });
  const [updateWorkoutSession, { loading: isSavingWorkoutProgress }] = useMutation(UPDATE_FITNESS_WORKOUT_SESSION, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }],
  });
  const [discardWorkout] = useMutation(DISCARD_FITNESS_WORKOUT, {
    refetchQueries: [{ query: GET_FITNESS_WORKOUTS, variables: { first: FITNESS_PAGE_SIZE, after: null } }],
  });

  /**
   * Normalizes input.
   *
   * @param {*} formData - form data value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const cleanInput = (formData) => ({
    title: formData.title.trim(),
    mode: formData.mode,
    workoutType: formData.workoutType,
    scheduledAt: formData.scheduledAt,
    completedAt: formData.completedAt,
    workoutDate: formData.status === 'Completed' ? formData.completedAt || formData.workoutDate : formData.scheduledAt || formData.workoutDate,
    activityType: formData.activityType || formData.workoutType,
    durationMinutes: Number(formData.durationMinutes || 0),
    intensity: formData.intensity,
    caloriesBurned: Number(formData.caloriesBurned || 0),
    distance: formData.distance.trim(),
    distanceUnit: formData.distanceUnit,
    pace: formData.pace.trim(),
    averageHeartRate: Number(formData.averageHeartRate || 0),
    maxHeartRate: Number(formData.maxHeartRate || 0),
    elevationGain: Number(formData.elevationGain || 0),
    equipment: splitCsv(formData.equipment),
    muscleGroups: splitCsv(formData.muscleGroups),
    strengthExercises: (formData.strengthExercises || []).map((exercise) => ({
      id: exercise.id,
      name: exercise.name.trim(),
      muscleGroup: exercise.muscleGroup.trim(),
      equipment: exercise.equipment.trim(),
      category: exercise.category || '',
      trackingMode: exercise.trackingMode || 'strength',
      weightUnit: exercise.weightUnit || 'lb',
      isCustom: Boolean(exercise.isCustom),
      notes: exercise.notes.trim(),
      sets: (exercise.sets || []).map((set) => ({
        id: set.id,
        reps: Number(set.reps || 0),
        weight: set.weight.trim(),
        durationSeconds: Number(set.durationSeconds || 0),
        distance: set.distance.trim(),
        restSeconds: Number(set.restSeconds || 0),
        completed: Boolean(set.completed),
      })),
    })),
    focusAreas: splitCsv(formData.focusAreas),
    className: formData.className.trim(),
    location: formData.location.trim(),
    coach: formData.coach.trim(),
    status: formData.status,
    plannedDate: formData.scheduledAt || formData.plannedDate,
    completedDate: formData.completedAt || formData.completedDate,
    planName: formData.planName.trim(),
    templateName: formData.templateName.trim(),
    energyLevel: formData.energyLevel,
    sorenessLevel: formData.sorenessLevel,
    recoveryNotes: formData.recoveryNotes.trim(),
    rating: formData.rating,
    review: formData.review.trim(),
    notes: '',
    tags: splitCsv(formData.tags),
    favorite: Boolean(formData.favorite),
  });

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);
    try {
      if (formData.id) {
        await updateWorkout({ variables: { id: formData.id, input } });
        navigate(`/hobby/fitness/${encodeURIComponent(formData.id)}`);
        return;
      }
      await addWorkout({ variables: { input } });
      navigate('/hobby/fitness');
    } catch (saveError) {
      if (isLimitReachedError(saveError)) {
        setShowPremiumUpsell(true);
        return;
      }
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  /**
   * Handles save plan.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.id - Stable identifier of the target resource.
   * @param {*} options.input - Values supplied to the operation.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSavePlan = async ({ id, input }) => {
    try {
      const result = id
        ? await updatePlan({ variables: { id, input } })
        : await addPlan({ variables: { input } });
      const saved = result.data?.updateFitnessPlan || result.data?.addFitnessPlan;
      setEditingPlan(null);
      setHasFormChanges(false);
      setIsAdding(false);
      setSelectedPlan(saved || null);
      navigate(saved?.id ? `/hobby/fitness/routine/${encodeURIComponent(saved.id)}` : '/hobby/fitness');
    } catch (saveError) {
      if (isLimitReachedError(saveError)) { setShowPremiumUpsell(true); return; }
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  /**
   * Handles delete plan.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleDeletePlan = async () => {
    if (!planPendingDelete) return;
    try {
      await deletePlan({ variables: { id: planPendingDelete.id } });
      setPlanPendingDelete(null);
      setSelectedPlan(null);
      navigate('/hobby/fitness');
    } catch (deleteError) {
      window.alert(getPublicErrorMessage(deleteError));
    }
  };

  /**
   * Performs the begin routine workout operation.
   *
   * @param {*} plan - plan value.
   * @param {*} day - day value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const beginRoutineWorkout = async (plan, day) => {
    if ((plan.days || []).length > 1 && !day) { setStartingPlan(plan); return; }
    const selectedDay = day || plan.days?.[0];
    try {
      const result = await startWorkout({ variables: { planId: plan.id, planDayId: selectedDay?.id || null } });
      const session = parseWorkout(result.data?.startFitnessWorkout);
      session.strengthExercises = (session.strengthExercises || []).map((exercise) => ({
        ...normalizeExercise(exercise),
        sets: normalizeExercise(exercise).sets.map((set) => ({ ...set, completed: false })),
      }));
      setStartingPlan(null);
      setActiveSession(session);
    } catch (startError) {
      window.alert(getPublicErrorMessage(startError));
    }
  };

  /**
   * Performs the session input operation.
   *
   * @param {*} session - session value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const sessionInput = (session) => ({
    durationMinutes: Number(session.durationMinutes || 0),
    caloriesBurned: Number(session.caloriesBurned || 0),
    distance: session.distance || '', distanceUnit: session.distanceUnit || 'mi',
    averageHeartRate: Number(session.averageHeartRate || 0), maxHeartRate: Number(session.maxHeartRate || 0),
    strengthExercises: (session.strengthExercises || []).map((exercise) => ({
      id: exercise.id, name: exercise.name, muscleGroup: exercise.muscleGroup || '', equipment: exercise.equipment || '',
      category: exercise.category || '', trackingMode: exercise.trackingMode || 'strength', weightUnit: exercise.weightUnit || 'lb',
      isCustom: Boolean(exercise.isCustom), notes: '', sets: exercise.sets.map((set) => ({
        id: set.id, reps: Number(set.reps || 0), weight: String(set.weight || ''), durationSeconds: Number(set.durationSeconds || 0),
        distance: String(set.distance || ''), restSeconds: Number(set.restSeconds || 0), completed: Boolean(set.completed),
      })),
    })),
    energyLevel: session.energyLevel || '', sorenessLevel: session.sorenessLevel || '', recoveryNotes: '',
    rating: session.rating || '', review: session.review || '', notes: '',
  });

  /**
   * Handles finish workout.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleFinishWorkout = async () => {
    if (!activeSession) return;
    const incomplete = activeSession.strengthExercises.some((exercise) => exercise.sets.some((set) => !set.completed));
    if (incomplete && !window.confirm('Some sets are incomplete. Finish this workout anyway?')) return;
    try {
      const result = await finishWorkout({
        variables: {
          id: activeSession.id,
          input: { ...sessionInput(activeSession), completedDate: localDateKey() },
        },
      });
      const saved = parseWorkout(result.data?.finishFitnessWorkout);
      if (saved.planId) await refetchRoutineHistory?.();
      setActiveSession(null);
      setSelectedWorkout(saved);
      navigate(`/hobby/fitness/${encodeURIComponent(saved.id)}`);
    } catch (finishError) {
      window.alert(getPublicErrorMessage(finishError));
    }
  };

  /**
   * Handles save workout progress.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSaveWorkoutProgress = async () => {
    if (!activeSession) return;
    try {
      const result = await updateWorkoutSession({ variables: { id: activeSession.id, input: sessionInput(activeSession) } });
      setActiveSession(parseWorkout(result.data?.updateFitnessWorkoutSession));
    } catch (saveError) {
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  /**
   * Handles discard workout.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleDiscardWorkout = async () => {
    if (!activeSession || !window.confirm('Discard workout? This unfinished workout will be deleted.')) return;
    await discardWorkout({ variables: { id: activeSession.id } });
    setActiveSession(null);
  };

  /**
   * Determines whether start create flow.
   *
   * @returns {boolean} Whether the requested condition is satisfied.
   */
  const canStartCreateFlow = () => {
    if (canCreateEntries(subscription, 'fitness')) return true;
    setShowPremiumUpsell(true);
    return false;
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canStartCreateFlow()) return;
    setIsAdding(true);
    navigate('/hobby/fitness/new');
  };

  useEffect(() => {
    if (isAddFlow || editingWorkout || isRoutineCreateRoute || editingPlan) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedPlan) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Routine actions"
          primaryAction={{ label: 'Edit', onSelect: () => navigate(`/hobby/fitness/routine/${encodeURIComponent(selectedPlan.id)}/edit`) }}
          actions={[
            { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('fitness', selectedPlan, 'routine')) },
            { label: 'Delete routine', variant: 'danger', onSelect: () => setPlanPendingDelete(selectedPlan) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedWorkoutDetail) {
      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Workout actions"
          primaryAction={{ label: 'Edit', disabled: isLoadingSelectedWorkout, onSelect: () => handleEditWorkout(selectedWorkoutDetail) }}
          actions={[
            (selectedWorkoutDetail.status === 'Completed' || selectedWorkoutDetail.completedAt) && { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('fitness', selectedWorkoutDetail)) },
            { label: fitnessStrings.module.deleteAction, variant: 'danger', onSelect: () => setWorkoutPendingDelete(selectedWorkoutDetail) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-primary" type="button" onClick={() => setShowAddChoice(true)}>Add</button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingPlan, editingWorkout, isAddFlow, isLoadingSelectedWorkout, isRoutineCreateRoute, navigate, onHeaderActionsChange, selectedPlan, selectedWorkoutDetail, subscription]);

  useEffect(() => {
    if (isRoutineEditRoute || editingPlan) {
      onHeaderTitleChange?.('Edit routine');
    } else if (isRoutineCreateRoute) {
      onHeaderTitleChange?.('Create routine');
    } else if (routePlanId || selectedPlan) {
      onHeaderTitleChange?.(selectedPlan?.title || 'Routine');
    } else if (isEditRoute || editingWorkout) {
      onHeaderTitleChange?.(fitnessStrings.module.editAction);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.(fitnessStrings.module.addAction);
    } else if (routeWorkoutId || selectedWorkoutDetail) {
      onHeaderTitleChange?.(selectedWorkoutDetail?.title || 'Workout');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingPlan, editingWorkout, isAdding, isCreateRoute, isEditRoute, isRoutineCreateRoute, isRoutineEditRoute, onHeaderTitleChange, routePlanId, normalizedWorkoutId, selectedPlan, selectedWorkoutDetail]);

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="fitness"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  /**
   * Renders fitness overlays.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderFitnessOverlays = () => (
    <>
      {showAddChoice && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowAddChoice(false)}>
          <section className="modal-card fitness-add-modal" role="dialog" aria-modal="true" aria-labelledby="fitness-add-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Fitness</p><h2 id="fitness-add-title">What do you want to add?</h2></div><button type="button" className="btn-icon" aria-label="Close" onClick={() => setShowAddChoice(false)}>×</button></div>
            <div className="fitness-add-options">
              <button type="button" onClick={() => { setShowAddChoice(false); setIsAdding(true); navigate('/hobby/fitness/new?mode=session'); }}><span className="fitness-option-icon">↗</span><span><strong>Log activity</strong><small>Record a walk, run, ride, swim, class, or sport.</small></span><b>›</b></button>
              <button type="button" onClick={() => { setShowAddChoice(false); setIsAdding(true); navigate('/hobby/fitness/new?mode=structured'); }}><span className="fitness-option-icon">◆</span><span><strong>Add workout</strong><small>Log a structured workout and its exercises.</small></span><b>›</b></button>
              <button type="button" onClick={() => { setShowAddChoice(false); navigate('/hobby/fitness/routine/new'); }}><span className="fitness-option-icon">▦</span><span><strong>Create routine</strong><small>Build a weekly plan with workout days.</small></span><b>›</b></button>
            </div>
          </section>
        </div>
      )}
      {startingPlan && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setStartingPlan(null)}>
          <section className="modal-card fitness-day-picker" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">{startingPlan.title}</p><h2>Choose workout</h2></div><button type="button" className="btn-icon" aria-label="Close" onClick={() => setStartingPlan(null)}>×</button></div>
            <div className="fitness-add-options">{(startingPlan.days || []).map((day) => <button type="button" key={day.id} disabled={isStartingWorkout} onClick={() => beginRoutineWorkout(startingPlan, day)}><span className="fitness-option-icon">◆</span><span><strong>{day.title || 'Workout day'}</strong><small>{[day.weekday, `${(day.strengthExercises || []).filter((item) => item.name).length} exercises`].filter(Boolean).join(' • ')}</small></span><b>›</b></button>)}</div>
          </section>
        </div>
      )}
      {activeSession && (
        <div className="modal-backdrop fitness-session-backdrop">
          <section className="modal-card fitness-session-modal" role="dialog" aria-modal="true" aria-labelledby="active-workout-title">
            <div className="modal-header"><div><p className="eyebrow">Workout in progress · {elapsedSessionTime(activeSession.startedAt, sessionClock)}</p><h2 id="active-workout-title">{activeSession.title}</h2></div></div>
            <div className="fitness-session-scroll">
              <FitnessExerciseEditor showsCompletion exercises={activeSession.strengthExercises || []} catalog={exerciseCatalog} onChange={(strengthExercises) => setActiveSession((current) => ({ ...current, strengthExercises }))} />
              <div className="form-grid two-columns"><div className="form-group"><label>Minutes</label><input type="number" min="0" value={activeSession.durationMinutes || ''} onChange={(event) => setActiveSession((current) => ({ ...current, durationMinutes: event.target.value }))} /></div><div className="form-group"><label>Calories</label><input type="number" min="0" value={activeSession.caloriesBurned || ''} onChange={(event) => setActiveSession((current) => ({ ...current, caloriesBurned: event.target.value }))} /></div></div>
            </div>
            <div className="modal-actions"><button type="button" className="btn-ghost danger-text" onClick={handleDiscardWorkout}>Discard</button><button type="button" className="btn-secondary" disabled={isSavingWorkoutProgress} onClick={handleSaveWorkoutProgress}>{isSavingWorkoutProgress ? 'Saving' : 'Save progress'}</button><button type="button" className="btn-primary" disabled={isFinishingWorkout} onClick={handleFinishWorkout}>{isFinishingWorkout ? 'Finishing' : 'Finish workout'}</button></div>
          </section>
        </div>
      )}
    </>
  );

  const closeForm = useCallback(() => {
    const editingWorkoutId = editingWorkout?.id;
    setEditingWorkout(null);
    setHasFormChanges(false);
    navigate(editingWorkoutId ? `/hobby/fitness/${encodeURIComponent(editingWorkoutId)}` : '/hobby/fitness');
    onClose();
  }, [editingWorkout?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm(fitnessStrings.module.discardConfirm);
      if (!shouldDiscard) return;
    }
    closeForm();
  }, [closeForm, hasFormChanges]);

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!workoutPendingDelete) return;
    await deleteWorkout({ variables: { id: workoutPendingDelete.id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!workoutsPageInfo.hasNextPage || !workoutsPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: FITNESS_PAGE_SIZE,
        after: workoutsPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getFitnessWorkoutsListPage) return previousResult;
        return {
          getFitnessWorkoutsListPage: {
            ...fetchMoreResult.getFitnessWorkoutsListPage,
            nodes: [
              ...(previousResult.getFitnessWorkoutsListPage?.nodes || []),
              ...(fetchMoreResult.getFitnessWorkoutsListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  const fetchWorkoutDetail = useCallback(async (workout) => {
    const result = await loadWorkoutDetail({ variables: { id: workout.id } });
    return parseWorkout(result.data?.getFitnessWorkout || workout);
  }, [loadWorkoutDetail]);

  /**
   * Handles select workout.
   *
   * @param {*} workout - workout value.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSelectWorkout = (workout) => {
    setSelectedWorkout(workout);
    navigate(`/hobby/fitness/${encodeURIComponent(workout.id)}`);
  };

  /**
   * Handles edit workout.
   *
   * @param {*} workout - workout value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditWorkout = async (workout) => {
    navigate(`/hobby/fitness/${encodeURIComponent(workout.id)}/edit`);
    setEditingWorkout(await fetchWorkoutDetail(workout));
  };

  useEffect(() => {
    if (isRoutineCreateRoute) {
      setSelectedPlan(null);
      setEditingPlan(null);
      setSelectedWorkout(null);
      setEditingWorkout(null);
      setIsAdding(false);
      return;
    }

    if (routePlanId) {
      const plan = plans.find((item) => item.id === routePlanId);
      if (!plan && !plansLoading) { navigate('/hobby/fitness', { replace: true }); return; }
      if (plan) {
        setSelectedWorkout(null);
        setEditingWorkout(null);
        setSelectedPlan(isRoutineEditRoute ? null : plan);
        setEditingPlan(isRoutineEditRoute ? plan : null);
      }
      setIsAdding(false);
      return;
    }

    if (isCreateRoute) {
      setSelectedPlan(null);
      setEditingPlan(null);
      setSelectedWorkout(null);
      setEditingWorkout(null);
      setIsAdding(true);
      return;
    }

    if (!normalizedWorkoutId) {
      setSelectedPlan(null);
      setEditingPlan(null);
      setSelectedWorkout(null);
      setEditingWorkout(null);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const workout = workouts.find((item) => item.id === normalizedWorkoutId) || { id: normalizedWorkoutId };
    fetchWorkoutDetail(workout)
      .then((detail) => {
        if (isEditRoute) {
          setSelectedWorkout(null);
          setEditingWorkout(detail);
          return;
        }
        setEditingWorkout(null);
        setSelectedWorkout(detail);
      })
      .catch(() => navigate('/hobby/fitness', { replace: true }));
  }, [fetchWorkoutDetail, isCreateRoute, isEditRoute, isRoutineCreateRoute, isRoutineEditRoute, navigate, normalizedWorkoutId, plans, plansLoading, routePlanId, setIsAdding, workouts]);

  useEffect(() => {
    if (!targetItemId || openedTargetRef.current === targetItemId) return;
    openedTargetRef.current = targetItemId;
    navigate(`/hobby/fitness/${encodeURIComponent(targetItemId)}`, { replace: true });
  }, [navigate, targetItemId]);

  useEffect(() => {
    if (isAddFlow || editingWorkout || isRoutineCreateRoute || editingPlan) {
      onRegisterCloseGuard?.(() => handleCancelForm({ confirmDiscard: true }));
      return () => onRegisterCloseGuard?.(null);
    }
    if (selectedWorkout || selectedPlan) {
      onRegisterCloseGuard?.(() => navigate('/hobby/fitness'));
      return () => onRegisterCloseGuard?.(null);
    }
    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingPlan, editingWorkout, handleCancelForm, isAddFlow, isRoutineCreateRoute, navigate, onRegisterCloseGuard, selectedPlan, selectedWorkout]);

  if (isRoutineCreateRoute || editingPlan) {
    return (
      <>
        <FitnessRoutineForm
          initialData={editingPlan}
          catalog={exerciseCatalog}
          isSaving={isAddingPlan || isUpdatingPlan}
          onSave={handleSavePlan}
          onDirtyChange={setHasFormChanges}
          onCancel={({ confirmDiscard } = {}) => {
            if (confirmDiscard && hasFormChanges && !window.confirm('Discard your unsaved routine changes?')) return;
            const target = editingPlan?.id ? `/hobby/fitness/routine/${encodeURIComponent(editingPlan.id)}` : '/hobby/fitness';
            setEditingPlan(null); setHasFormChanges(false); navigate(target);
          }}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (isAddFlow || editingWorkout) {
    return (
      <>
        <FitnessForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          isSaving={isSaving || isUpdating}
          initialData={editingWorkout || (searchParams.get('mode') === 'session' ? { mode: 'session', workoutType: 'Walk', activityType: 'Walk' } : null)}
          onDirtyChange={setHasFormChanges}
          exerciseCatalog={exerciseCatalog}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedPlan) {
    return (
      <>
        <FitnessRoutineDetail
          plan={selectedPlan}
          workouts={workouts}
          history={routineHistoryData?.getFitnessRoutineHistory?.nodes || []}
          historyLoading={routineHistoryLoading}
          historyPageInfo={routineHistoryData?.getFitnessRoutineHistory?.pageInfo}
          onLoadMoreHistory={async () => {
            const pageInfo = routineHistoryData?.getFitnessRoutineHistory?.pageInfo;
            if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
            await fetchMoreRoutineHistory({
              variables: { planId: selectedPlan.id, first: 10, after: pageInfo.endCursor },
              updateQuery: (previous, { fetchMoreResult }) => {
                const current = previous?.getFitnessRoutineHistory;
                const next = fetchMoreResult?.getFitnessRoutineHistory;
                if (!next) return previous;
                const existingIds = new Set((current?.nodes || []).map((entry) => entry.id));
                return { getFitnessRoutineHistory: { ...next, nodes: [...(current?.nodes || []), ...next.nodes.filter((entry) => !existingIds.has(entry.id))] } };
              },
            });
          }}
          onBack={() => navigate('/hobby/fitness')}
          onStart={(day) => beginRoutineWorkout(selectedPlan, day)}
          onLogPast={async (day, completedDate) => {
            const result = await logRoutineWorkout({
              variables: { planId: selectedPlan.id, planDayId: day.id, completedDate },
            });
            await refetchRoutineHistory();
            return result.data?.logFitnessRoutineWorkout;
          }}
          onOpenWorkout={(workout) => navigate(`/hobby/fitness/${encodeURIComponent(workout.id)}`)}
        />
        {planPendingDelete && <div className="modal-backdrop"><section className="modal-card delete-confirmation" role="dialog" aria-modal="true"><p className="eyebrow">Delete routine</p><h2>Delete routine?</h2><p>This will permanently remove “{planPendingDelete.title}”. Completed workouts will remain in your activity history.</p><div className="modal-actions"><button type="button" className="btn-ghost" onClick={() => setPlanPendingDelete(null)}>Cancel</button><button type="button" className="btn-danger solid" disabled={isDeletingPlan} onClick={handleDeletePlan}>{isDeletingPlan ? 'Deleting' : 'Delete routine'}</button></div></section></div>}
        {renderFitnessOverlays()}
      </>
    );
  }

  if (selectedWorkoutDetail) {
    const detailItems = compactDetailItems([
      ['Mode', modeLabel(selectedWorkoutDetail.mode)],
      ['Type', selectedWorkoutDetail.workoutType],
      ['Status', selectedWorkoutDetail.status],
      [selectedWorkoutDetail.status === 'Completed' ? 'Completed on' : 'Planned for', formatDate(primaryDate(selectedWorkoutDetail))],
      ['Duration', minutesLabel(selectedWorkoutDetail.durationMinutes)],
      ['Effort', selectedWorkoutDetail.intensity],
      ['Distance', selectedWorkoutDetail.distance && `${selectedWorkoutDetail.distance} ${selectedWorkoutDetail.distanceUnit || ''}`.trim()],
      ['Calories', selectedWorkoutDetail.caloriesBurned > 0 ? `${selectedWorkoutDetail.caloriesBurned} cal` : ''],
      ['Average HR', selectedWorkoutDetail.averageHeartRate > 0 ? `${selectedWorkoutDetail.averageHeartRate} bpm` : ''],
      ['Max HR', selectedWorkoutDetail.maxHeartRate > 0 ? `${selectedWorkoutDetail.maxHeartRate} bpm` : ''],
      ['Elevation', selectedWorkoutDetail.elevationGain > 0 ? `${selectedWorkoutDetail.elevationGain}` : ''],
      ['Location', selectedWorkoutDetail.location],
      ['Coach', selectedWorkoutDetail.coach],
    ]);
    const visibleExercises = selectedWorkoutDetail.strengthExercises.filter((exercise) => exercise?.name);
    const hasReview = selectedWorkoutDetail.rating || selectedWorkoutDetail.review;

    return (
      <div className="module-wrapper fitness-module movies-module">
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/fitness')}>← Back</button>

          {workoutPendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{fitnessStrings.module.deleteTitle}</p>
                <h2>{fitnessStrings.module.deletePrompt}</h2>
                <p>{fitnessStrings.module.deleteDetail(workoutPendingDelete.title)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setWorkoutPendingDelete(null)}>{moduleCommonStrings.actions.cancel}</button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? moduleCommonStrings.actions.deleting : fitnessStrings.module.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero">
            <EntryImage
              className="entry-detail-photo"
              fallbackType={selectedWorkoutDetail.mode === 'session' ? 'fitness-activity' : 'fitness-workout'}
              variant="detail-square"
            />
            <div>
              <p className="eyebrow">{modeLabel(selectedWorkoutDetail.mode)} · {formatDate(primaryDate(selectedWorkoutDetail))}</p>
              <h2>{selectedWorkoutDetail.title}</h2>
              <p>{[selectedWorkoutDetail.workoutType, selectedWorkoutDetail.intensity].filter(Boolean).join(' · ')}</p>
              {selectedWorkoutDetail.source === 'apple_health' && <span className="source-chip">Synced from Apple Health{selectedWorkoutDetail.sourceDevice ? ` • ${selectedWorkoutDetail.sourceDevice}` : ''}</span>}
            </div>
            <div className="recipe-detail-facts">
              <span>{minutesLabel(selectedWorkoutDetail.durationMinutes)}</span>
              <span>{selectedWorkoutDetail.status}</span>
              <span>{selectedWorkoutDetail.intensity}</span>
              {selectedWorkoutDetail.distance && <span>{`${selectedWorkoutDetail.distance} ${selectedWorkoutDetail.distanceUnit || ''}`.trim()}</span>}
              {selectedWorkoutDetail.caloriesBurned > 0 && <span>{selectedWorkoutDetail.caloriesBurned} cal</span>}
              {selectedWorkoutDetail.rating && <RatingReadout rating={selectedWorkoutDetail.rating} />}
              {selectedWorkoutDetail.favorite && <span>{fitnessStrings.module.favorite}</span>}
            </div>
          </section>

          <section className="recipe-detail-panel">
            <h3>{fitnessStrings.form.sections.details}</h3>
            <dl className="detail-grid">
              {detailItems.map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}
            </dl>
          </section>

          {selectedWorkoutDetail.mode === 'structured' && visibleExercises.length > 0 && (
            <section className="recipe-detail-panel">
              <h3>{fitnessStrings.form.sections.exercises}</h3>
              <div className="fitness-exercise-list">
                {visibleExercises.map((exercise) => (
                  <div className="fitness-exercise-card" key={exercise.id || exercise.name}>
                    <strong>{exercise.name}</strong>
                    <span>{[exercise.muscleGroup, exercise.equipment].filter(Boolean).join(' · ')}</span>
                    <div className="recipe-tags">
                      {(exercise.sets || []).map((set, index) => (
                        <span key={set.id || index}>{[set.reps ? `${set.reps} reps` : '', set.weight, set.restSeconds ? `${set.restSeconds}s rest` : ''].filter(Boolean).join(' / ')}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {selectedWorkoutDetail.mode === 'session' && (selectedWorkoutDetail.planName || selectedWorkoutDetail.location || selectedWorkoutDetail.pace) && (
            <section className="recipe-detail-panel">
              <h3>{fitnessStrings.form.sections.sessionLog}</h3>
              {selectedWorkoutDetail.planName && <p className="detail-notes">{selectedWorkoutDetail.planName}</p>}
            </section>
          )}

          {hasReview && (
            <section className="recipe-detail-panel">
              <h3>{fitnessStrings.form.sections.review}</h3>
              {selectedWorkoutDetail.rating && <RatingReadout rating={selectedWorkoutDetail.rating} />}
              {selectedWorkoutDetail.review && <p className="detail-notes">{selectedWorkoutDetail.review}</p>}
            </section>
          )}

          {[...selectedWorkoutDetail.tags, ...selectedWorkoutDetail.muscleGroups, ...selectedWorkoutDetail.focusAreas].length > 0 && (
            <section className="recipe-detail-panel">
              <h3>{fitnessStrings.form.sections.tags}</h3>
              <div className="recipe-tags">
                {[...selectedWorkoutDetail.tags, ...selectedWorkoutDetail.muscleGroups, ...selectedWorkoutDetail.focusAreas].map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            </section>
          )}
        </div>
        {renderFitnessOverlays()}
      </div>
    );
  }

  return (
    <div className="module-wrapper fitness-module movies-module">
      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{fitnessStrings.module.activityEyebrow}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeMode && !activeType ? 'active' : ''} onClick={() => { setActiveMode(''); setActiveType(''); }}>
              <span>{fitnessStrings.module.allActivities}</span>
              <strong>{workouts.length + plans.length}</strong>
            </button>
            <button type="button" className={activeMode === 'structured' ? 'active' : ''} onClick={() => { setActiveMode('structured'); setActiveType(''); }}>
              <span>{fitnessStrings.module.structured}</span>
              <strong>{workouts.filter((workout) => workout.mode === 'structured').length}</strong>
            </button>
            <button type="button" className={activeMode === 'session' ? 'active' : ''} onClick={() => { setActiveMode('session'); setActiveType(''); }}>
              <span>{fitnessStrings.module.sessions}</span>
              <strong>{workouts.filter((workout) => workout.mode === 'session').length}</strong>
            </button>
            <button type="button" className={activeMode === 'routine' ? 'active' : ''} onClick={() => { setActiveMode('routine'); setActiveType(''); }}>
              <span>Routines</span>
              <strong>{plans.length}</strong>
            </button>
            {activityTypes.map((type) => (
              <button type="button" key={type} className={activeType === type ? 'active' : ''} onClick={() => { setActiveType(type); setActiveMode(''); }}>
                <span>{type}</span>
                <strong>{workouts.filter((workout) => workout.workoutType === type || workout.activityType === type).length}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{fitnessStrings.module.searchLabel}</label>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={fitnessStrings.module.searchPlaceholder} />
            </div>
            <div className="filter-row trip-filter-row">
              <label>
                {fitnessStrings.module.tagLabel}
                <select value={activeTag} onChange={(event) => setActiveTag(event.target.value)}>
                  <option value="">{fitnessStrings.module.anyTag}</option>
                  {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                </select>
              </label>
              <button type="button" className={`favorite-filter ${favoritesOnly ? 'active' : ''}`} onClick={() => setFavoritesOnly((current) => !current)}>
                {moduleCommonStrings.filters.favorites}
              </button>
            </div>
          </div>

          {(loading || plansLoading) && workouts.length === 0 && plans.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {(error || plansError) && <p className="error-copy">Error: {getPublicErrorMessage(error || plansError)}</p>}

          <div className="recipe-stats">
            <span>{fitnessStrings.module.shown(filteredWorkouts.length + filteredPlans.length)}</span>
            <span>{fitnessStrings.module.minutes(totalMinutes)}</span>
            <span>{fitnessStrings.module.completed(workouts.filter((workout) => workout.status === 'Completed').length)}</span>
          </div>

          <div className="recipe-card-grid">
            {filteredPlans.map((plan) => {
              const completedThisWeek = new Set(workouts.filter((workout) => workout.planId === plan.id && workout.status === 'Completed').map((workout) => workout.planDayId).filter(Boolean)).size;
              return <button key={plan.id} type="button" className="recipe-card fitness-card fitness-routine-card has-entry-photo" onClick={() => navigate(`/hobby/fitness/routine/${encodeURIComponent(plan.id)}`)}>
                <EntryImage fallbackType="fitness-routine" />
                <div><div className="movie-card-heading"><h3>{plan.title}</h3>{plan.favorite && <span className="favorite-chip">Favorite</span>}</div><p>{['Routine', plan.goal, `${plan.daysPerWeek || plan.days?.length || 0}x per week`].filter(Boolean).join(' • ')}</p><p className="movie-card-detail-line">{`${completedThisWeek} of ${plan.days?.length || 0} workouts complete`}</p></div>
              </button>;
            })}
            {filteredWorkouts.map((workout) => (
              <button key={workout.id} type="button" className="recipe-card fitness-card has-entry-photo" onClick={() => handleSelectWorkout(workout)}>
                <EntryImage fallbackType={workout.mode === 'session' ? 'fitness-activity' : 'fitness-workout'} />
                <div>
                  <div className="movie-card-heading">
                    <h3>{workout.title}</h3>
                    {workout.source === 'apple_health' && <span className="source-chip">Apple Health</span>}
                    {workout.favorite && <span className="favorite-chip">{fitnessStrings.module.favorite}</span>}
                  </div>
                  <p>{[modeLabel(workout.mode), workout.workoutType || workout.activityType, formatDate(primaryDate(workout))].filter(Boolean).join(' • ')}</p>
                  <p className="movie-card-detail-line">
                    {[workout.status, minutesLabel(workout.durationMinutes), workoutMetric(workout), workout.intensity].filter(Boolean).join(' • ') || fitnessStrings.module.noNotes}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {workoutsPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? 'Loading activities...' : 'Load more activities'}
              </button>
            </div>
          )}

          {filteredWorkouts.length + filteredPlans.length === 0 && !loading && !plansLoading && (
            <HobbyEmptyState hobbyId="fitness" filtered={workouts.length + plans.length > 0}
              title={workouts.length + plans.length === 0 ? moduleCommonStrings.empty.nothingSaved : moduleCommonStrings.empty.noMatches}
              detail={workouts.length + plans.length === 0 ? 'Log your first activity or create a routine when you are ready.' : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
      {renderPremiumUpsell()}
      {renderFitnessOverlays()}
    </div>
  );
}
